import { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './Schedules.css';

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const Schedules = () => {
  usePageTitle('Schedules');
  const [schedules, setSchedules] = useState([]);
  const [flows, setFlows] = useState([]);
  const [runProfiles, setRunProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, schedule: null });
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    target_type: 'flow',
    flow: '',
    run_profile: '',
    frequency: 'daily',
    run_mode: 'incremental',
    weekdays: [],
    day_of_month: 1,
    specific_date: '',
    start_date: '',
    end_date: '',
    trigger_times: ['09:00'],
    is_active: true
  });

  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [schedulesRes, flowsRes, profilesRes] = await Promise.all([
        axiosInstance.get('/etl/schedules/'),
        axiosInstance.get('/etl/flows/'),
        axiosInstance.get('/etl/run-profiles/')
      ]);
      setSchedules(schedulesRes.data);
      setFlows(flowsRes.data.filter(f => f.status === 'active'));
      setRunProfiles(profilesRes.data.filter(p => p.status === 'active'));
      setLoading(false);
    } catch (err) {
      setError('Failed to load schedules');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Prepare payload
    const payload = {
      ...formData,
      // Only include relevant fields based on frequency
      weekdays: formData.frequency === 'weekly' ? formData.weekdays : [],
      day_of_month: formData.frequency === 'monthly' ? formData.day_of_month : null,
      specific_date: formData.frequency === 'specific_date' ? formData.specific_date : null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      // Only include the relevant target based on target_type
      flow: formData.target_type === 'flow' ? formData.flow : null,
      run_profile: formData.target_type === 'run_profile' ? formData.run_profile : null,
    };

    try {
      if (editingSchedule) {
        await axiosInstance.put(`/etl/schedules/${editingSchedule.id}/`, payload);
      } else {
        await axiosInstance.post('/etl/schedules/', payload);
      }
      setShowModal(false);
      setEditingSchedule(null);
      resetForm();
      fetchData();
    } catch (err) {
      alert('Failed to save schedule: ' + (err.response?.data?.error || JSON.stringify(err.response?.data) || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleSchedule = async (schedule) => {
    try {
      await axiosInstance.post(`/etl/schedules/${schedule.id}/toggle/`);
      fetchData();
    } catch (err) {
      alert('Failed to toggle schedule: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteSchedule = async () => {
    if (!deleteModal.schedule) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/etl/schedules/${deleteModal.schedule.id}/`);
      setDeleteModal({ show: false, schedule: null });
      fetchData();
    } catch (err) {
      alert('Failed to delete schedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      target_type: schedule.target_type || 'flow',
      flow: schedule.flow || '',
      run_profile: schedule.run_profile || '',
      frequency: schedule.frequency,
      run_mode: schedule.run_mode || 'incremental',
      weekdays: schedule.weekdays || [],
      day_of_month: schedule.day_of_month || 1,
      specific_date: schedule.specific_date || '',
      start_date: schedule.start_date || '',
      end_date: schedule.end_date || '',
      trigger_times: schedule.trigger_times || ['09:00'],
      is_active: schedule.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      target_type: 'flow',
      flow: '',
      run_profile: '',
      frequency: 'daily',
      run_mode: 'incremental',
      weekdays: [],
      day_of_month: 1,
      specific_date: '',
      start_date: '',
      end_date: '',
      trigger_times: ['09:00'],
      is_active: true
    });
    setNewTime('');
  };

  const openNewModal = () => {
    setEditingSchedule(null);
    resetForm();
    setShowModal(true);
  };

  const addTriggerTime = () => {
    if (newTime && !formData.trigger_times.includes(newTime)) {
      setFormData({
        ...formData,
        trigger_times: [...formData.trigger_times, newTime].sort()
      });
      setNewTime('');
    }
  };

  const removeTriggerTime = (timeToRemove) => {
    setFormData({
      ...formData,
      trigger_times: formData.trigger_times.filter(t => t !== timeToRemove)
    });
  };

  const toggleWeekday = (day) => {
    const newWeekdays = formData.weekdays.includes(day)
      ? formData.weekdays.filter(d => d !== day)
      : [...formData.weekdays, day].sort((a, b) => a - b);
    setFormData({ ...formData, weekdays: newWeekdays });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getFlowName = (flowId) => {
    const flow = flows.find(f => f.id === flowId);
    return flow ? flow.name : 'Unknown Flow';
  };

  // Stats
  const stats = {
    total: schedules.length,
    active: schedules.filter(s => s.is_active).length,
    inactive: schedules.filter(s => !s.is_active).length
  };

  if (loading) {
    return (
      <div className="schedules-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading schedules...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedules-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="schedules-container">
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, schedule: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Schedule</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteModal.schedule?.name}</strong>?</p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteModal({ show: false, schedule: null })}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={deleteSchedule}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content schedule-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Basic Info */}
                <div className="form-section">
                  <h4>Basic Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Schedule Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Daily Full Refresh"
                        required
                      />
                    </div>
                  </div>

                  {/* Target Type Selection */}
                  <div className="form-group">
                    <label>What to Schedule</label>
                    <div className="target-type-selector">
                      <button
                        type="button"
                        className={`target-type-btn ${formData.target_type === 'flow' ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, target_type: 'flow', run_profile: '' })}
                      >
                        <span className="target-icon">📊</span>
                        <span>Single Flow</span>
                      </button>
                      <button
                        type="button"
                        className={`target-type-btn ${formData.target_type === 'run_profile' ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, target_type: 'run_profile', flow: '' })}
                      >
                        <span className="target-icon">📦</span>
                        <span>Run Profile</span>
                      </button>
                    </div>
                    <span className="form-hint">
                      {formData.target_type === 'flow'
                        ? 'Schedule a single ETL flow'
                        : 'Schedule a run profile (batch of flows/pipelines)'}
                    </span>
                  </div>

                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{formData.target_type === 'flow' ? 'Select Flow' : 'Select Run Profile'}</label>
                      {formData.target_type === 'flow' ? (
                        <select
                          value={formData.flow}
                          onChange={(e) => setFormData({ ...formData, flow: e.target.value })}
                          required
                        >
                          <option value="">Select a flow...</option>
                          {flows.map(flow => (
                            <option key={flow.id} value={flow.id}>
                              {flow.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={formData.run_profile}
                          onChange={(e) => setFormData({ ...formData, run_profile: e.target.value })}
                          required
                        >
                          <option value="">Select a run profile...</option>
                          {runProfiles.map(profile => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name} ({profile.item_count || 0} items)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Run Mode</label>
                      <select
                        value={formData.run_mode}
                        onChange={(e) => setFormData({ ...formData, run_mode: e.target.value })}
                      >
                        <option value="incremental">Incremental</option>
                        <option value="full">Full Refresh</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Frequency */}
                <div className="form-section">
                  <h4>Frequency</h4>
                  <div className="frequency-options">
                    {[
                      { value: 'daily', label: 'Daily', icon: '📅' },
                      { value: 'weekly', label: 'Weekly', icon: '📆' },
                      { value: 'monthly', label: 'Monthly', icon: '🗓️' },
                      { value: 'specific_date', label: 'Specific Date', icon: '📌' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`frequency-btn ${formData.frequency === opt.value ? 'active' : ''}`}
                        onClick={() => setFormData({ ...formData, frequency: opt.value })}
                      >
                        <span className="freq-icon">{opt.icon}</span>
                        <span>{opt.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Weekly - Day Selection */}
                  {formData.frequency === 'weekly' && (
                    <div className="form-group weekday-selector">
                      <label>Select Days</label>
                      <div className="weekday-buttons">
                        {WEEKDAYS.map(day => (
                          <button
                            key={day.value}
                            type="button"
                            className={`weekday-btn ${formData.weekdays.includes(day.value) ? 'active' : ''}`}
                            onClick={() => toggleWeekday(day.value)}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly - Day of Month */}
                  {formData.frequency === 'monthly' && (
                    <div className="form-group">
                      <label>Day of Month</label>
                      <select
                        value={formData.day_of_month}
                        onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>
                            {day}{day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Specific Date */}
                  {formData.frequency === 'specific_date' && (
                    <div className="form-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={formData.specific_date}
                        onChange={(e) => setFormData({ ...formData, specific_date: e.target.value })}
                        required={formData.frequency === 'specific_date'}
                      />
                    </div>
                  )}
                </div>

                {/* Date Range */}
                <div className="form-section">
                  <h4>Active Period <span className="optional">(optional)</span></h4>
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                      <span className="form-hint">Schedule activates from this date</span>
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                      <span className="form-hint">Schedule stops after this date</span>
                    </div>
                  </div>
                </div>

                {/* Trigger Times */}
                <div className="form-section">
                  <h4>Trigger Times</h4>
                  <p className="section-desc">Add multiple times when the pipeline should run each day</p>

                  <div className="trigger-times-list">
                    {formData.trigger_times.map((time, index) => (
                      <div key={index} className="trigger-time-chip">
                        <span>{time}</span>
                        <button
                          type="button"
                          className="remove-time-btn"
                          onClick={() => removeTriggerTime(time)}
                          disabled={formData.trigger_times.length === 1}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                            <path d="M4.293 4.293a1 1 0 011.414 0L7 5.586l1.293-1.293a1 1 0 111.414 1.414L8.414 7l1.293 1.293a1 1 0 01-1.414 1.414L7 8.414l-1.293 1.293a1 1 0 01-1.414-1.414L5.586 7 4.293 5.707a1 1 0 010-1.414z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="add-time-row">
                    <input
                      type="time"
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      className="time-input"
                    />
                    <button
                      type="button"
                      className="btn-add-time"
                      onClick={addTriggerTime}
                      disabled={!newTime}
                    >
                      Add Time
                    </button>
                  </div>

                  <div className="quick-times">
                    <span className="quick-label">Quick add:</span>
                    {['06:00', '09:00', '12:00', '15:00', '18:00', '21:00'].map(time => (
                      <button
                        key={time}
                        type="button"
                        className={`quick-time-btn ${formData.trigger_times.includes(time) ? 'added' : ''}`}
                        onClick={() => {
                          if (!formData.trigger_times.includes(time)) {
                            setFormData({
                              ...formData,
                              trigger_times: [...formData.trigger_times, time].sort()
                            });
                          }
                        }}
                        disabled={formData.trigger_times.includes(time)}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingSchedule ? 'Update Schedule' : 'Create Schedule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Schedules</h1>
          <p className="page-description">Automate your ETL pipelines with scheduled runs</p>
        </div>
        <button onClick={openNewModal} className="btn-primary">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          New Schedule
        </button>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Schedules</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-value active">{stats.active}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-value inactive">{stats.inactive}</span>
          <span className="stat-label">Inactive</span>
        </div>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
        </svg>
        <div>
          <strong>Note:</strong> Schedules require a background task runner (Celery Beat) to execute automatically.
          Currently, you can use schedules as a reference for when to manually trigger flows or integrate with external schedulers.
        </div>
      </div>

      {/* Schedules Table */}
      {schedules.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="12" y="16" width="40" height="40" rx="4" />
            <path d="M12 28H52" />
            <path d="M24 12V20" />
            <path d="M40 12V20" />
            <circle cx="32" cy="42" r="4" />
          </svg>
          <h3>No schedules yet</h3>
          <p>Create your first schedule to automate pipeline runs</p>
          <button onClick={openNewModal} className="btn-primary">
            Create Schedule
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="schedules-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Target</th>
                <th>Schedule</th>
                <th>Run Mode</th>
                <th>Next Run</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule) => (
                <tr key={schedule.id} className={!schedule.is_active ? 'inactive-row' : ''}>
                  <td>
                    <button
                      className={`toggle-btn ${schedule.is_active ? 'active' : ''}`}
                      onClick={() => toggleSchedule(schedule)}
                      title={schedule.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <span className="toggle-slider"></span>
                    </button>
                  </td>
                  <td>
                    <span className="schedule-name">{schedule.name}</span>
                  </td>
                  <td>
                    <div className="target-cell">
                      <span className={`target-badge ${schedule.target_type === 'run_profile' ? 'profile' : 'flow'}`}>
                        {schedule.target_type === 'run_profile' ? 'Profile' : 'Flow'}
                      </span>
                      <span className="target-name">{schedule.target_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="schedule-summary">
                      {schedule.schedule_summary || '-'}
                    </div>
                    {schedule.trigger_times && schedule.trigger_times.length > 0 && (
                      <div className="trigger-times-preview">
                        {schedule.trigger_times.slice(0, 3).map((time, i) => (
                          <span key={i} className="time-badge">{time}</span>
                        ))}
                        {schedule.trigger_times.length > 3 && (
                          <span className="more-times">+{schedule.trigger_times.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`mode-badge ${schedule.run_mode}`}>
                      {schedule.run_mode === 'full' ? 'Full' : 'Incr'}
                    </span>
                  </td>
                  <td>
                    <span className="date-text">{formatDate(schedule.next_run_at)}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        onClick={() => openEditModal(schedule)}
                        title="Edit Schedule"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 00-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 000-.354l-1.086-1.086zM11.189 6.25L9.75 4.81l-6.286 6.287a.25.25 0 00-.064.108l-.558 1.953 1.953-.558a.249.249 0 00.108-.064l6.286-6.286z" />
                        </svg>
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => setDeleteModal({ show: true, schedule })}
                        title="Delete Schedule"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 00.249.225h5.19a.25.25 0 00.249-.225l.66-6.6A.75.75 0 1011.82 6l-.66 6.6A1.75 1.75 0 0110.595 14H5.405a1.75 1.75 0 01-1.744-1.575L3 6.75a.75.75 0 00-1.5.075l.66 6.6A3.25 3.25 0 005.405 15.5h5.19a3.25 3.25 0 003.245-3.075l.66-6.6A.75.75 0 1014.25 6l-.66 6.6z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Schedules;
